// ═══════════════════════════════════════
// accionesPermiso.js — catálogo de ACCIONES por módulo (v2)
// ═══════════════════════════════════════
// Fuente de verdad: MODULOS_CATALOGO en `modulos.js`.
// Este archivo expone vistas DERIVADAS pensadas para consumirse desde:
//   - El editor del rol (matriz módulo × acción).
//   - La función puedeHacer() para validar que una acción exista en el módulo.
//   - El frontend cuando renderiza botones condicionales.
//
// Regla dura: una acción NO puede inventarse en runtime. Si querés agregar
// una acción nueva, va al `MODULOS_CATALOGO` + corres `sincronizarModulosConFirestore`.
// Esto evita drift entre código y BD.
// ═══════════════════════════════════════

import { MODULOS_CATALOGO, PERMISO_SCOPES } from './modulos'
// #87 — La tabla MODULO_EJE_FALLBACK y el cache runtime viven en `_ejesCache.js`
// (separado para evitar circular con modulos.js — ese archivo también necesita
// el fallback al sincronizar /modulos en Firestore).
import { ejeDeFallback } from './_ejesCache'

/**
 * Devuelve un map { moduloCodigo: [funcionCodigo, ...] } con todas las acciones
 * disponibles por módulo. Lectura O(1) por módulo.
 */
export const ACCIONES_PERMISO = Object.freeze(
  Object.fromEntries(
    MODULOS_CATALOGO.map(m => [
      m.codigo,
      Object.freeze(m.funciones.map(f => f.codigo)),
    ])
  )
)

/**
 * Devuelve un map { moduloCodigo: { funcionCodigo: 'Nombre legible' } } para
 * renderizar la UI del editor de permisos.
 */
export const ACCIONES_LABEL = Object.freeze(
  Object.fromEntries(
    MODULOS_CATALOGO.map(m => [
      m.codigo,
      Object.freeze(
        Object.fromEntries(m.funciones.map(f => [f.codigo, f.nombre]))
      ),
    ])
  )
)

/**
 * Lista todos los módulos del sistema (códigos), ordenados por `orden`.
 */
export function listarModulos() {
  return MODULOS_CATALOGO
    .slice()
    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
    .map(m => m.codigo)
}

/**
 * Nombre legible de un módulo del catálogo (buanwIkr: el dropdown de /permisos
 * mostraba solo el código — "mercado" no se encontraba buscando "Explorador").
 * Retorna el código si el módulo no está en el catálogo (fail honesto).
 */
const _NOMBRE_MODULO = Object.freeze(
  Object.fromEntries(MODULOS_CATALOGO.map(m => [m.codigo, m.nombre || m.codigo]))
)
export function nombreDeModulo(moduloCodigo) {
  return _NOMBRE_MODULO[moduloCodigo] || String(moduloCodigo)
}

/**
 * Devuelve las acciones disponibles para un módulo. Retorna [] si el módulo no existe.
 */
export function accionesDe(moduloCodigo) {
  return ACCIONES_PERMISO[moduloCodigo] || []
}

/**
 * Valida que (modulo, accion) sea un par válido conocido por el catálogo.
 * Útil para que el editor del rol no permita guardar acciones inválidas.
 */
export function esAccionValida(moduloCodigo, accionCodigo) {
  const acciones = ACCIONES_PERMISO[moduloCodigo]
  return Array.isArray(acciones) && acciones.includes(accionCodigo)
}

/**
 * Re-export de scopes para que el editor los consuma desde un solo lugar.
 */
export { PERMISO_SCOPES }

/**
 * Lista plana de scopes válidos (para validación).
 */
export const SCOPES_VALIDOS = Object.freeze(Object.values(PERMISO_SCOPES))

// ─────────────────────────────────────────────────────────────────
// EJES — clasificación de módulos (Operativo vs Plataforma)
// ─────────────────────────────────────────────────────────────────
// Ver docs/MODELO_ORGANIZACIONAL.md §2 "Dos ejes de roles".
//   - operativo:  módulos sobre datos del negocio de las empresas.
//   - plataforma: módulos sobre la gestión del sistema mismo.
// Este mapa vive acá (un solo lugar) en vez de en cada entrada de
// MODULOS_CATALOGO — más fácil de mantener.

export const EJES = Object.freeze({
  OPERATIVO: 'operativo',
  PLATAFORMA: 'plataforma',
})

/**
 * Devuelve el eje ('operativo' | 'plataforma') de un módulo.
 * La tabla de defaults y el cache runtime viven en `_ejesCache.js`.
 * Resolución: cache Firestore → MODULO_EJE_FALLBACK → MODULO_EJE_SIN_DECIDIR → 'plataforma'.
 * El último default es fail-closed a propósito (report LVyJbRGvZebhNU2MmO4G): ver
 * `_ejesCache.js > ejeDeFallback`.
 */
export function ejeDe(moduloCodigo) {
  return ejeDeFallback(moduloCodigo)
}

/**
 * Lista los códigos de módulos de un eje, ordenados por `orden`.
 * @param {'operativo'|'plataforma'} eje
 * @returns {string[]}
 */
export function modulosPorEje(eje) {
  return MODULOS_CATALOGO
    .filter(m => ejeDe(m.codigo) === eje)
    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
    .map(m => m.codigo)
}
